import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Modal } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import './LoanApplicationDocumentView.css';
import { useGetLoanApplication } from '../../../services/loanService';
import { downloadDocument } from '../../../services/documentService';
import DocumentValidityBadge from '../../common/DocumentValidityBadge';
import DocumentActionsMenu from '../../common/DocumentActionsMenu';
import { formatDate } from '../../../utils/formatters';
import { DocumentKind, resolveForPreview, withExtension } from '../../../utils/documentPreview';
import { getDocumentUploadDate, getDocumentUploader } from '../../../utils/documentDates';
import StatementIqPanel from '../../loan-application/steps/StatementIqPanel';

const LoanApplicationDocumentView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadingDocuments, setDownloadingDocuments] = useState<Set<string>>(new Set());

  // Fetch loan application data
  const { data: application, isLoading, isError, error } = useGetLoanApplication(id || '');

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'VERIFIED':
        return <Badge bg="success">Verified</Badge>;
      case 'PENDING':
        return <Badge bg="warning">Pending</Badge>;
      case 'REJECTED':
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getDocumentIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'pdf':
        return 'bi-file-pdf';
      case 'docx':
      case 'doc':
        return 'bi-file-word';
      case 'xlsx':
      case 'xls':
        return 'bi-file-excel';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'bi-file-image';
      default:
        return 'bi-file-earmark';
    }
  };

  const [previewingKey, setPreviewingKey] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ name: string; kind: DocumentKind; url: string } | null>(null);

  const handleView = async (documentKey: string, documentName: string) => {
    try {
      setPreviewingKey(documentKey);
      const blob = await downloadDocument(documentKey);
      const { kind, blob: typed } = await resolveForPreview(blob, documentName);
      setPreview({ name: documentName, kind, url: window.URL.createObjectURL(typed) });
    } catch (error) {
      console.error('Error opening document:', error);
      toast.error('Could not open the document');
    } finally {
      setPreviewingKey(null);
    }
  };

  const closePreview = () => {
    if (preview) window.URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const handleDownload = async (documentKey: string, documentName: string) => {
    try {
      // Add document to downloading set
      setDownloadingDocuments(prev => new Set(prev).add(documentKey));

      // Download the document using documentKey
      const blob = await downloadDocument(documentKey);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = await withExtension(documentName, blob);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Document downloaded successfully');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    } finally {
      // Remove document from downloading set
      setDownloadingDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentKey);
        return newSet;
      });
    }
  };

  // Use documents from application data
  const documents = application?.documents || [];

  // Format document type to display friendly names
  const formatDocumentType = (docType: string): string => {
    if (!docType) return 'Other';
    
    switch (docType) {
      case 'ID_DOCUMENT':
        return 'ID Document';
      case 'BANK_STATEMENT':
        return 'Bank Statement';
      case 'BANK_LETTER':
        return 'Bank Letter';
      case 'TAX_CLEARANCE':
        return 'Tax Clearance';
      case 'FINANCIAL_STATEMENTS':
        return 'Financial Statements';
      case 'COMPANY_REGISTRATION_CERTIFICATE':
        return 'Company Registration';
      case 'PROOF_OF_ADDRESS':
        return 'Proof of Address';
      case 'PAYSLIP':
        return 'Payslip';
      case 'COLLATERAL_DOCUMENT':
        return 'Collateral Document';
      case 'LOAN_AGREEMENT':
        return 'Loan Agreement';
      case 'CIPC_COR_14_3':
        return 'CIPC COR 14.3';
      case 'DIRECTOR_ID_COPY':
        return 'Director ID Copy';
      case 'COMPANY_REP_ID_COPY':
        return 'Company Rep ID Copy';
      case 'DELEGATION_OF_AUTHORITY':
        return 'Delegation of Authority';
      case 'TAX_CLEARANCE_PIN':
        return 'Tax Clearance PIN';
      case 'PROOF_OF_CONTRACT':
        return 'Proof of Contract';
      case 'OTHER':
        return 'Other';
      default:
        return docType.replace(/_/g, ' ').toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    }
  };

  // Determine document status based on loan application status
  const getDocumentStatus = (docStatus: string, applicationStatus: string | undefined): string => {
    // If document has explicit status, use it
    if (docStatus && ['VERIFIED', 'PENDING', 'REJECTED'].includes(docStatus)) {
      return docStatus;
    }
    
    // Otherwise determine status based on application status
    if (applicationStatus) {
      // Verified statuses
      if (['EVALUATION_IN_PROGRESS', 'AGREEMENT_UPLOAD', 'COLLATERAL_VERIFICATION', 
           'PENDING_DISBURSEMENT', 'ACTIVE'].includes(applicationStatus)) {
        return 'VERIFIED';
      }
      
      // Rejected statuses
      if (['REJECTED_EVALUATION', 'REJECTED_COLLATERAL'].includes(applicationStatus)) {
        return 'REJECTED';
      }
    }
    
    // Default to pending
    return 'PENDING';
  };

  const filteredDocuments = documents.filter((doc: any) => {
    return !searchQuery || 
      doc.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      formatDocumentType(doc.documentType)?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.createdBy?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const columns = [
    {
      name: 'Document',
      selector: (row: any) => row.name,
      sortable: true,
      cell: (row: any) => (
        <div className="d-flex align-items-center">
          <i className={`bi ${getDocumentIcon(row.documentType)} me-2 text-primary`}></i>
          <div>
            <div className="fw-bold">{row.name}</div>
            <small className="text-muted">{formatDocumentType(row.documentType)}</small>
          </div>
        </div>
      ),
      grow: 2,
    },
    {
      name: 'Status',
      selector: (row: any) => getDocumentStatus(row.status, application?.status),
      sortable: true,
      cell: (row: any) => getStatusBadge(getDocumentStatus(row.status, application?.status)),
      center: true,
    },
    {
      name: 'Uploaded By',
      // Who first uploaded the file and when -- carried through reuse, so a document attached from
      // an earlier application still shows its real upload, not the day it was attached here.
      selector: (row: any) => getDocumentUploader(row) || '',
      sortable: true,
      cell: (row: any) => (
        <div>
          <div>{getDocumentUploader(row) || 'System'}</div>
          <small className="text-muted">
            {getDocumentUploadDate(row) ? formatDate(getDocumentUploadDate(row)) : 'Unknown'}
          </small>
          <div className="mt-1">
            <DocumentValidityBadge uploadDate={getDocumentUploadDate(row)} />
          </div>
        </div>
      ),
    },
    {
      name: 'Size',
      selector: (row: any) => row.size,
      sortable: true,
      cell: (row: any) => formatFileSize(row.size),
      center: true,
    },
    {
      name: 'Actions',
      cell: (row: any) => {
        return (
          <DocumentActionsMenu
            id={row.id || row.documentKey}
            actions={[
              {
                label: 'View',
                icon: 'fa-eye',
                busy: previewingKey === row.documentKey,
                disabled: !row.documentKey,
                onClick: () => handleView(row.documentKey, row.name)
              },
              {
                label: 'Download',
                icon: 'fa-download',
                busy: downloadingDocuments.has(row.documentKey),
                disabled: !row.documentKey,
                onClick: () => handleDownload(row.documentKey, row.name)
              }
            ]}
          />
        );
      },
      center: true,
      ignoreRowClick: true,
    },
  ];

  // Helper function to format file size
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';

    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 Byte';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const customStyles = {
    table: {
      style: {
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #e9ecef',
        borderRadius: '12px 12px 0 0',
      },
    },
    rows: {
      style: {
        minHeight: '72px',
      },
    },
    cells: {
      style: {
        padding: '1rem',
      },
    },
  };

  // If there's an error, display error state
  if (isError) {
    return (
      <div className="document-view-page">
        <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
          <div className="container-fluid">
            <div className="row g-3 align-items-center">
              <div className="col">
                <div className="d-flex align-items-center">
                  <button
                    className="btn btn-link me-3"
                    onClick={() => navigate('/documents')}
                  >
                    <i className="bi bi-arrow-left"></i>
                  </button>
                  <h4 className="mb-0">
                    <i className="bi bi-file-earmark-text me-2 text-primary"></i>
                    Document Explorer
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
          <div className="container-fluid">
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="mb-4">
                  <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '4rem' }}></i>
                </div>
                <h2 className="mb-3">Unable to Load Documents</h2>
                <p className="text-muted mb-4">
                  We encountered an error while trying to load the application documents.
                </p>
                <p className="text-danger mb-4">
                  {(error as any)?.message || 'Failed to retrieve application data. Please try again later.'}
                </p>
                <div className="d-flex justify-content-center gap-3">
                  <button
                    className="btn btn-primary rounded-pill px-4 py-2"
                    onClick={() => window.location.reload()}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>Try Again
                  </button>
                  <button
                    className="btn btn-outline-secondary rounded-pill px-4 py-2"
                    onClick={() => navigate('/documents')}
                  >
                    <i className="bi bi-arrow-left me-2"></i>Back to Documents
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="document-view-page">
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <div className="row g-3 align-items-center">
            <div className="col">
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-link me-3"
                  onClick={() => navigate('/documents')}
                >
                  <i className="bi bi-arrow-left"></i>
                </button>
                <h4 className="mb-0">
                  <i className="bi bi-file-earmark-text me-2 text-primary"></i>
                  Document Explorer
                </h4>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          <Row>
            {/* Summary Section */}
            <Col lg={12} className="mb-4">
              <Card className="summary-card">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <div className="summary-icon bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                      <i className="bi bi-file-earmark-text text-primary" style={{ fontSize: '1.25rem' }}></i>
                    </div>
                    <div>
                      <h5 className="mb-1">Application Details</h5>
                      <p className="text-muted mb-0">Loan application information and status</p>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-4">
                    <div className="detail-item">
                      <div className="text-muted small mb-1">Customer Name</div>
                      <div className="fw-bold">
                        {application?.company?.companyName ||
                         application?.individual?.fullNames ||
                         'Loading...'}
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="text-muted small mb-1">Application Number</div>
                      <div className="fw-bold">#{application?.applicationNumber || 'N/A'}</div>
                    </div>
                    <div className="detail-item">
                      <div className="text-muted small mb-1">Loan Amount</div>
                      <div className="fw-bold">
                        R {application?.loanDetails?.loanAmount?.toLocaleString() || 'N/A'}
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="text-muted small mb-1">Application Date</div>
                      <div className="fw-bold">{application?.createdDate || 'N/A'}</div>
                    </div>
                    <div className="detail-item">
                      <div className="text-muted small mb-1">Total Documents</div>
                      <div className="fw-bold">{documents.length}</div>
                    </div>
                    <div className="detail-item">
                      <div className="text-muted small mb-1">Verified Documents</div>
                      <div className="fw-bold">
                        {documents.filter((doc: any) => doc.status === 'VERIFIED').length}
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="text-muted small mb-1">Pending Documents</div>
                      <div className="fw-bold">
                        {documents.filter((doc: any) => doc.status === 'PENDING').length}
                      </div>
                    </div>
                  </div>
                  <div className="progress mt-3" style={{ height: '4px' }}>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{
                        width: documents.length
                          ? `${(documents.filter((doc: any) => doc.status === 'VERIFIED').length / documents.length) * 100}%`
                          : '0%'
                      }}
                    ></div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {application?.loanType === 'PERSONAL' && id && <Col lg={12}>
              <StatementIqPanel applicationId={id} readOnly />
            </Col>}

            {/* Document Table */}
            <Col lg={12}>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">Document List</h5>
                    <div className="search-box">
                      <div className="input-group">
                        <span className="input-group-text bg-transparent border-end-0">
                          <i className="bi bi-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="Search documents..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <DataTable
                    columns={columns}
                    data={filteredDocuments}
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    sortServer
                    customStyles={customStyles}
                    progressPending={isLoading}
                    progressComponent={
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading documents...</p>
                      </div>
                    }
                    noDataComponent={
                      <div className="text-center py-5">
                        <i className="bi bi-folder-x text-muted" style={{ fontSize: '3rem' }}></i>
                        <p className="mt-3 text-muted">No documents found</p>
                      </div>
                    }
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal show={!!preview} onHide={closePreview} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 text-truncate">{preview?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 bg-body-secondary" style={{ minHeight: '60vh' }}>
          {preview?.kind === 'pdf' && (
            <iframe
              src={preview.url}
              title={preview.name}
              style={{ width: '100%', height: '75vh', border: 0 }}
            />
          )}
          {preview?.kind === 'image' && (
            <div className="d-flex justify-content-center align-items-center p-3" style={{ minHeight: '60vh' }}>
              <img src={preview.url} alt={preview.name} style={{ maxWidth: '100%', maxHeight: '72vh' }} />
            </div>
          )}
          {preview?.kind === 'other' && (
            <div className="text-center text-muted p-5">
              <i className="bi bi-file-earmark" style={{ fontSize: '2.5rem' }}></i>
              <p className="mt-3 mb-0">This file type cannot be shown here. Download it to open it.</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {preview && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                const link = document.createElement('a');
                link.href = preview.url;
                link.download = preview.name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              <i className="bi bi-download me-1"></i>
              Download
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={closePreview}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LoanApplicationDocumentView;
